import {
  forwardRef,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import styled from 'styled-components';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Autocomplete as AutocompleteBase,
  AutocompleteProps,
  Paper as PaperBase,
  PaperProps as PaperPropsBase,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  AutocompleteOwnerState,
  Box,
  Divider,
  Typography,
} from '@mui/material';
import { ReactComponent as MoreIcon } from '../../assets/more.svg';
import { ComponentSize } from '../../types/types';
import IconButton from '../IconButton';
import TextField from '../TextField';
import Menu from '../Menu';
import Chip from '../Chip';
import { Flex } from '../Flex';
import FormControls from '../FormControls';

const StyledTextField = styled(TextField)<{
  $variant?: 'borderless';
  $chipDisplay?: boolean;
  $hasValue?: boolean;
}>`
  .MuiOutlinedInput-root {
    height: auto;

    .MuiAutocomplete-endAdornment {
      top: 50%;
      transform: translateY(-50%);
    }

    ${({ $chipDisplay, $variant }) =>
      $chipDisplay &&
      `
      flex-wrap: nowrap;
      padding-right: 0 !important;
      ${$variant === 'borderless' ? 'padding-left: 0;' : ''}
  
      .MuiAutocomplete-endAdornment {
        position: static;
        top: auto;
        transform: none;
        margin-left: -4px;
      }
    `}

    &.Mui-disabled {
      .MuiOutlinedInput-notchedOutline {
        background: ${({ theme }) => theme.palette.common.gray10} !important;
        border: none !important;
      }
    }

    ${({ $variant }) =>
      $variant === 'borderless' &&
      `
      .MuiOutlinedInput-notchedOutline {
        border: none !important;
      }

      &:hover .MuiOutlinedInput-notchedOutline {
        border: none !important;
      }

      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border: none !important;
      }
    `}

    ${({ $chipDisplay, $hasValue }) =>
      (!$chipDisplay || ($chipDisplay && $hasValue)) &&
      `
      .MuiAutocomplete-input {
        min-width: 0 !important;
        width: 0 !important;
      }
    `}
  }
`;

const StyledActionItem = styled.div`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.common.blue};
  padding: 10px 16px;
  border-radius: 8px;
  background-color: white;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.common.gray9};
  }
`;

const InvalidChipWrapper = styled.span`
  display: inline-block;
  text-decoration: underline;
  text-decoration-style: dashed;
  text-decoration-color: #d32f2f;
  padding: 4px 8px;
  cursor: pointer;
`;

interface PaperProps extends PaperPropsBase {
  children?: ReactNode;
  actions?: string[];
  onAction?: (actionValue: string) => void;
  handleClose: (event: React.SyntheticEvent, reason: string) => void;
}

const Paper = ({
  children,
  actions,
  onAction,
  handleClose,
  ...paperProps
}: PaperProps) => (
  <PaperBase {...paperProps}>
    {actions && actions.length > 0 && (
      <Box onMouseDown={(e) => e.preventDefault()} sx={{ padding: '4px' }}>
        {actions.map((action) => (
          <StyledActionItem
            key={action}
            onClick={(e) => {
              e.stopPropagation();
              onAction?.(action);
              handleClose(e, 'selectOption');
            }}
          >
            {action}
          </StyledActionItem>
        ))}
        <Box
          sx={{
            borderBottom: (theme) => `1px solid ${theme.palette.common.gray8}`,
            margin: '4px 0',
          }}
        />
      </Box>
    )}
    {children}
  </PaperBase>
);

const OptionWithActions = ({
  props: listItemProps,
  option,
  keepOpen,
  onOptionAction,
  closeAutocomplete,
}: {
  props: React.HTMLAttributes<HTMLLIElement>;
  option: any;
  keepOpen: React.MutableRefObject<boolean>;
  onOptionAction?: (optionValue: string, actionValue: string) => void;
  closeAutocomplete: () => void;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const optionValue =
    typeof option === 'string' ? option : option.value ?? option;

  return (
    <li
      {...listItemProps}
      className={`${listItemProps.className || ''} ${
        isMenuOpen ? 'menu-open' : ''
      }`}
    >
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {option.label || option}
        </span>
        {option.actions && option.actions.length > 0 && (
          <Menu
            menuButton={
              <IconButton
                className="actions-menu-button"
                sx={{ p: '10px 6px !important' }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  keepOpen.current = true;
                }}
              >
                <MoreIcon />
              </IconButton>
            }
            options={option.actions}
            onOpen={() => setIsMenuOpen(true)}
            onChange={(actionValue) => {
              if (onOptionAction) {
                onOptionAction(optionValue, actionValue as string);
              }
              setIsMenuOpen(false);
              keepOpen.current = false;
              closeAutocomplete();
            }}
            onClose={() => {
              setIsMenuOpen(false);
              keepOpen.current = false;
            }}
          />
        )}
      </Flex>
    </li>
  );
};

const renderTags = (value: readonly any[], getTagProps: any) =>
  value.map((option: any, index: number) => {
    // @ts-ignore
    const { key, ...tagProps } = getTagProps({ index });
    return (
      <Chip
        key={key}
        customVariant="rounded"
        label={option.label || option}
        icon={option.icon}
        bold
        {...tagProps}
      />
    );
  });

type RenderTagsWithValidationConfig = {
  valueValidator: (value: string) => boolean;
  isInputControlled: boolean;
  setLocalInputValue: (value: string) => void;
  propsOnInputChange?: AutocompleteProps<any, any, any, any>['onInputChange'];
  onChange?: (event: any, value: any, reason: any, details?: any) => void;
  open: boolean;
  isControlled: boolean;
  setLocalOpen: (open: boolean) => void;
};

const renderTagsWithValidation =
  (config: RenderTagsWithValidationConfig) =>
  (tagValue: readonly any[], getTagProps: any) =>
    tagValue.map((option, index) => {
      const { key, ...chipProps } = getTagProps({ index });
      const isValid = config.valueValidator(
        typeof option === 'string' ? option : option.value,
      );
      const label = typeof option === 'string' ? option : option.label;
      const icon = typeof option === 'string' ? undefined : option.icon;

      if (!isValid) {
        return (
          <InvalidChipWrapper
            key={key}
            onClick={(e) => {
              e.stopPropagation();
              const newValue = tagValue.filter((_, i) => i !== index);
              if (!config.isInputControlled) {
                config.setLocalInputValue(label);
              }
              config.propsOnInputChange?.(e, label, 'input');
              config.onChange?.(e, newValue, 'removeOption', { option });
              if (!config.open && !config.isControlled) {
                config.setLocalOpen(true);
              }
            }}
          >
            {label}
          </InvalidChipWrapper>
        );
      }

      return (
        <Flex
          key={key}
          sx={{
            overflow: 'hidden',
            height: '100%',
            alignItems: 'center',
          }}
        >
          <Chip
            customVariant="rounded"
            label={label}
            icon={icon}
            bold
            {...chipProps}
          />
        </Flex>
      );
    });

const renderInput = (
  params: AutocompleteRenderInputParams,
  {
    ref,
    autoFocus,
    placeholder,
    variant,
    chipDisplay,
    multiple,
    hasValue,
    value,
    size,
  }: {
    ref: any;
    autoFocus?: boolean;
    placeholder?: string;
    variant?: 'borderless';
    chipDisplay?: boolean;
    multiple?: boolean;
    hasValue: boolean;
    value: any;
    size?: ComponentSize;
  },
) => {
  // For single-select, show the selected value's icon as startAdornment
  const selectedIcon =
    !multiple && value && typeof value === 'object' && value.icon
      ? value.icon
      : undefined;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { size: _muiSize, ...restParams } = params;

  return (
    <StyledTextField
      ref={ref}
      {...restParams}
      fullWidth
      size={size}
      autoFocus={autoFocus}
      placeholder={hasValue ? undefined : placeholder}
      $variant={variant}
      $chipDisplay={chipDisplay && !multiple}
      $hasValue={hasValue}
      inputProps={{
        ...params.inputProps,
        readOnly:
          chipDisplay && !multiple && value !== null && value !== undefined,
      }}
      InputProps={{
        ...params.InputProps,
        startAdornment: selectedIcon ? (
          <>
            <Flex sx={{ ml: 1, mr: 0.5 }}>{selectedIcon}</Flex>
            {params.InputProps?.startAdornment}
          </>
        ) : (
          params.InputProps?.startAdornment
        ),
      }}
    />
  );
};

type RenderOptionProp = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: any,
  state: AutocompleteRenderOptionState,
  ownerState: AutocompleteOwnerState<any, any, any, any>,
) => React.ReactNode;

type RenderOptionConfig = {
  renderOptionProp?: RenderOptionProp;
  keepOpen: React.MutableRefObject<boolean>;
  onOptionAction?: (optionValue: string, actionValue: string) => void;
  isControlled: boolean;
  setLocalOpen: (open: boolean) => void;
};

const renderOption = (
  optionProps: React.HTMLAttributes<HTMLLIElement>,
  option: any,
  state: AutocompleteRenderOptionState,
  ownerState: AutocompleteOwnerState<any, any, any, any>,
  {
    renderOptionProp,
    keepOpen,
    onOptionAction,
    isControlled,
    setLocalOpen,
  }: RenderOptionConfig,
) => {
  if (option?.isEmptyPlaceholder) {
    return (
      <Box
        component="li"
        {...optionProps}
        sx={{
          pointerEvents: 'none',
          '&:hover': { backgroundColor: 'transparent !important' },
        }}
      >
        <Typography sx={{ color: 'common.gray4' }}>{option.label}</Typography>
      </Box>
    );
  }

  if (renderOptionProp) {
    return renderOptionProp(optionProps, option, state, ownerState);
  }

  return (
    <OptionWithActions
      props={optionProps}
      option={option}
      keepOpen={keepOpen}
      onOptionAction={onOptionAction}
      closeAutocomplete={() => {
        if (!isControlled) {
          setLocalOpen(false);
        }
      }}
    />
  );
};

const Autocomplete = forwardRef(
  (
    {
      placeholder,
      onOptionAction,
      actions,
      onAction,
      open: propsOpen,
      onOpen: propsOnOpen,
      onClose: propsOnClose,
      value,
      disableClearable = false,
      variant,
      listboxMaxWidth,
      listboxMaxHeight,
      listboxTitle,
      multiple,
      chipDisplay = false,
      onChange,
      showEmptyMessage = true,
      customEmptyMessage,
      valueValidator,
      invalidMessage,
      inputValue: propsInputValue,
      onInputChange: propsOnInputChange,
      size = 'medium',
      options = [],
      groups,
      groupEmptyText,
      groupBy: groupByProp,
      ...props
    }: Omit<AutocompleteProps<any, any, any, any>, 'renderInput' | 'size'> & {
      size?: ComponentSize;
      onOptionAction?: (optionValue: string, actionValue: string) => void;
      actions?: string[];
      onAction?: (actionValue: string) => void;
      variant?: 'borderless';
      listboxMaxWidth?: string | number;
      listboxMaxHeight?: string | number;
      listboxTitle?: string;
      chipDisplay?: boolean;
      showEmptyMessage?: boolean;
      customEmptyMessage?: ReactNode;
      valueValidator?: (value: string) => boolean;
      invalidMessage?: string;
      groups?: string[];
      groupEmptyText?: string;
    },
    ref,
  ) => {
    const keepOpen = useRef(false);
    const [localOpen, setLocalOpen] = useState(false);
    const [localInputValue, setLocalInputValue] = useState('');
    const [placeholderWidth, setPlaceholderWidth] = useState(0);

    const isControlled = propsOpen !== undefined;
    const isInputControlled = propsInputValue !== undefined;
    const inputValue = isInputControlled ? propsInputValue : localInputValue;
    const open = isControlled ? propsOpen : localOpen;

    // When chipDisplay is true, we use multiple mode internally but restrict to single value
    const internalMultiple = multiple || chipDisplay;

    const selectedOption = useMemo(() => {
      if (!value || !options) return value;
      if (Array.isArray(value)) {
        return value.map((v) => {
          const val = typeof v === 'object' ? v.value : v;
          return (
            options.find(
              (opt) => (typeof opt === 'object' ? opt.value : opt) === val,
            ) || v
          );
        });
      }
      const val = typeof value === 'object' ? value.value : value;
      return (
        options.find(
          (opt) => (typeof opt === 'object' ? opt.value : opt) === val,
        ) || value
      );
    }, [value, options]);

    const augmentedOptions = useMemo(() => {
      if (!groups?.length || !groupEmptyText || !groupByProp) return options;
      const existingGroups = new Set(options.map((opt) => groupByProp(opt)));
      const result: typeof options[number][] = [];
      groups.forEach((group) => {
        const groupOptions = options.filter(
          (opt) => groupByProp(opt) === group,
        );
        if (groupOptions.length > 0) {
          result.push(...groupOptions);
        } else if (!existingGroups.has(group)) {
          result.push({
            isEmptyPlaceholder: true,
            __placeholderGroup: group,
            label: groupEmptyText,
            value: `__empty_${group}`,
          });
        }
      });
      options.forEach((opt) => {
        if (!groups.includes(groupByProp(opt))) result.push(opt);
      });
      return result;
    }, [options, groups, groupEmptyText, groupByProp]);

    const internalGroupBy = groupByProp
      ? (option: {
          isEmptyPlaceholder?: boolean;
          __placeholderGroup?: string;
          [key: string]: any;
        }) =>
          option.isEmptyPlaceholder
            ? option.__placeholderGroup ?? ''
            : groupByProp(option)
      : undefined;

    const internalValue =
      chipDisplay && !multiple
        ? selectedOption !== null && selectedOption !== undefined
          ? [selectedOption]
          : []
        : selectedOption;

    const hasValue = Array.isArray(internalValue)
      ? internalValue.length > 0
      : internalValue !== null && internalValue !== undefined;

    useLayoutEffect(() => {
      if (chipDisplay && !hasValue && placeholder) {
        const span = document.createElement('span');
        span.style.visibility = 'hidden';
        span.style.position = 'absolute';
        span.style.whiteSpace = 'nowrap';
        span.style.fontSize = '14px';
        span.style.fontFamily = 'Inter, sans-serif';
        span.textContent = placeholder;
        document.body.appendChild(span);
        // 45px to account for padding and arrow icon
        setPlaceholderWidth(span.offsetWidth + 45);
        document.body.removeChild(span);
      }
    }, [chipDisplay, hasValue, placeholder]);

    const handleOpen = (event: React.SyntheticEvent) => {
      if (!isControlled) {
        setLocalOpen(true);
      }
      propsOnOpen?.(event);
    };

    const handleClose = (event: React.SyntheticEvent, reason: any) => {
      if (keepOpen.current) {
        return;
      }
      if (!isControlled) {
        setLocalOpen(false);
      }
      propsOnClose?.(event, reason);
    };

    const handleChange = (
      event: React.SyntheticEvent,
      newValue: any,
      reason: any,
      details?: any,
    ) => {
      let processedValue = newValue;

      // Sort values when validator is present: valid items first, invalid items last
      if (valueValidator && Array.isArray(newValue) && newValue.length > 0) {
        const validItems: any[] = [];
        const invalidItems: any[] = [];

        newValue.forEach((item: any) => {
          const valueToValidate =
            typeof item === 'string' ? item : item?.value || '';
          if (valueValidator(valueToValidate)) {
            validItems.push(item);
          } else {
            invalidItems.push(item);
          }
        });

        processedValue = [...validItems, ...invalidItems];
      }

      const toStorableValue = (v: any) =>
        v && typeof v === 'object' ? { value: v.value, label: v.label } : v;

      if (chipDisplay && !multiple) {
        // In chipDisplay mode (single selection), extract the single value from array
        // Take the last item since MUI adds the new selection to the end
        const singleValue = Array.isArray(processedValue)
          ? processedValue[processedValue.length - 1] || null
          : processedValue;
        onChange?.(event, toStorableValue(singleValue), reason, details);
      } else {
        const valueToStore = Array.isArray(processedValue)
          ? processedValue.map(toStorableValue)
          : toStorableValue(processedValue);
        onChange?.(event, valueToStore, reason, details);
      }
    };

    const minWidth = useMemo(
      () => (!hasValue && placeholderWidth ? `${placeholderWidth}px` : '0px'),
      [hasValue, placeholderWidth],
    );

    const hasInvalidValues =
      valueValidator &&
      Array.isArray(internalValue) &&
      internalValue.some((item: any) => {
        const valueToValidate =
          typeof item === 'string' ? item : item?.value || '';
        return !valueValidator(valueToValidate);
      });

    return (
      <>
        <AutocompleteBase
          sx={{ minWidth }}
          aria-label="autocomplete"
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          multiple={internalMultiple}
          value={internalValue}
          onChange={handleChange}
          inputValue={inputValue}
          size={size === 'large' ? 'medium' : 'small'}
          options={augmentedOptions}
          groupBy={internalGroupBy}
          getOptionDisabled={(option) =>
            !!option?.isEmptyPlaceholder ||
            !!(props.getOptionDisabled && props.getOptionDisabled(option))
          }
          onInputChange={(event, newInputValue, reason) => {
            if (!isInputControlled) {
              setLocalInputValue(newInputValue);
            }
            propsOnInputChange?.(event, newInputValue, reason);
          }}
          PaperComponent={(paperProps) => (
            <Paper
              {...paperProps}
              actions={actions}
              onAction={onAction}
              handleClose={handleClose}
            />
          )}
          ListboxComponent={
            listboxTitle
              ? forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>(
                  (listboxProps, listboxRef) => (
                    <ul {...listboxProps} ref={listboxRef}>
                      <Box
                        component="li"
                        sx={{
                          padding: '6px 16px',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: 'common.gray4',
                          pointerEvents: 'none',
                        }}
                      >
                        {listboxTitle}
                      </Box>
                      {listboxProps.children}
                    </ul>
                  ),
                )
              : undefined
          }
          renderTags={
            valueValidator
              ? renderTagsWithValidation({
                  valueValidator,
                  isInputControlled,
                  setLocalInputValue,
                  propsOnInputChange,
                  onChange,
                  open,
                  isControlled,
                  setLocalOpen,
                })
              : renderTags
          }
          renderInput={(params) =>
            renderInput(params, {
              ref,
              autoFocus: props.autoFocus,
              placeholder,
              variant,
              chipDisplay,
              multiple,
              hasValue,
              value: selectedOption,
              size,
            })
          }
          renderOption={(optionProps, option, state, ownerState) =>
            renderOption(optionProps, option, state, ownerState, {
              renderOptionProp: props.renderOption,
              keepOpen,
              onOptionAction,
              isControlled,
              setLocalOpen,
            })
          }
          componentsProps={{
            popper: {
              sx: {
                '&.MuiPopper-root': { mt: '8px !important' },
                ...(listboxMaxWidth && { width: 'fit-content !important' }),
                ...(!showEmptyMessage && {
                  '.MuiAutocomplete-noOptions': {
                    display: 'none !important',
                  },
                }),
              },
              ...(listboxMaxWidth && {
                placement: 'bottom-start',
              }),
            },
          }}
          ListboxProps={{
            sx: {
              padding: '4px',
              ...(listboxMaxWidth && { maxWidth: listboxMaxWidth }),
              ...(listboxMaxHeight && { maxHeight: listboxMaxHeight }),
              '.MuiAutocomplete-option': {
                display: 'block',
                fontSize: '14px',
                padding: '10px 16px',
                borderRadius: '8px',
                backgroundColor: 'white !important',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '.actions-menu-button': {
                  visibility: 'hidden',
                },
                ':hover': {
                  backgroundColor: (theme) =>
                    `${theme.palette.common.gray9} !important`,
                  '.actions-menu-button': {
                    visibility: 'visible',
                  },
                },
                '&.menu-open .actions-menu-button': {
                  visibility: 'visible',
                },
              },
              '& .Mui-focusVisible': {
                backgroundColor: (theme) =>
                  `${theme.palette.common.gray9} !important`,
              },
              ...(!showEmptyMessage && {
                '.MuiAutocomplete-noOptions': {
                  display: 'none',
                },
              }),
            },
          }}
          renderGroup={(params) => (
            <Box key={params.key}>
              {Number(params.key) > 0 && <Divider sx={{ my: 0.5 }} />}
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '12px',
                  color: 'common.gray11',
                  padding: '8px 16px 4px',
                }}
              >
                {params.group}
              </Typography>
              {params.children}
            </Box>
          )}
          popupIcon={<KeyboardArrowDown />}
          disableClearable={disableClearable}
          noOptionsText={
            showEmptyMessage
              ? customEmptyMessage
                ? customEmptyMessage
                : undefined
              : null
          }
          {...props}
        />
        {hasInvalidValues && (
          <FormControls.ErrorMessage>
            {invalidMessage || 'Invalid values selected.'}
          </FormControls.ErrorMessage>
        )}
      </>
    );
  },
);

export default Autocomplete;
